import { createApi } from '@reduxjs/toolkit/query/react';

import { customFetchBase } from '../../utils/authentication/fetchBaseQuery';
import {
    transformErrorResponse,
    transformSuccessResponse
} from '../../utils/middleware/responseMiddleware2';
import { encryptAES } from '../../utils/encryption/CryptoService';

const IS_ENCRYPTION_ENABLED = process.env.REACT_APP_IS_ENCRYPTION === 'true';

const stableStringify = (obj) => {
    if (typeof obj !== 'object' || obj === null) {
        return String(obj);
    }
    if (Array.isArray(obj)) {
        return JSON.stringify(obj);
    }
    return JSON.stringify(
        Object.keys(obj)
            .sort((a, b) => a.localeCompare(b))
            .reduce((acc, key) => {
                acc[key] = obj[key];
                return acc;
            }, {})
    );
};

export const commonApi = createApi({
    reducerPath: 'commonApi',
    baseQuery: customFetchBase,

    tagTypes: ['GridData', 'DropdownData', 'GeneralData', 'FileData'],
    keepUnusedDataFor: 600,
    refetchOnReconnect: true,
    refetchOnMountOrArgChange: false,

    endpoints: (builder) => ({

        getGridData: builder.query({
            query: (params) => {
                const { url } = params;
                return {
                    url,

                    params: IS_ENCRYPTION_ENABLED
                        ? params?.params
                        : { data: JSON.stringify(params?.params) }
                };
            },
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse
        }),


        getDropdownData: builder.query({
            query: (params) => ({
                url: params.url,
                params: params.params
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,

            serializeQueryArgs: ({ queryArgs }) => {
                const url = queryArgs.url;
                const params = (queryArgs.params && Object.keys(queryArgs.params).length > 0)
                    ? stableStringify(queryArgs.params)
                    : "{}";
                return `${url}_${params}`;
            },
            providesTags: (result, error, arg) => [{ type: 'DropdownData', id: arg.url }],
        }),

        postGridData: builder.mutation({
            query: (params) => {
                const { url } = params;
                const { first, rows, sortField, sortOrder, ...rest } = params.params;

                rest.pageIndex = Math.floor(first / rows);
                rest.pageSize = rows;

                if (sortField) {
                    rest.sortField = sortField;
                    rest.sortDesc = sortOrder < 0;
                }

                return {
                    url,
                    body: rest,
                    method: 'POST'
                };
            },
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse
        }),

        getData: builder.query({

            query: (params) => {
                return {
                    url: params.url,
                    params: params.data
                };
            },
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
            serializeQueryArgs: ({ queryArgs }) => {
                const url = queryArgs.url;
                const params = queryArgs.data
                    ? stableStringify(queryArgs.data)
                    : "{}";

                return `${url}_${params}`;
            },

            providesTags: (result, error, arg) => [
                { type: 'GeneralData', id: `${arg.url}_${stableStringify(arg.data)}` }
            ],
        }),

        deleteData: builder.mutation({
            query: ({ url, data }) => ({
                url,
                method: "DELETE",
                params: { data: data }
            }),
            invalidatesTags: (result, error, params) => [
                { type: 'GridData', id: params.url },
                { type: 'GeneralData', id: params.url }
            ],
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse
        }),

        restoreData: builder.mutation({
            query: ({ url, data }) => ({
                url,
                method: "POST",
                params: IS_ENCRYPTION_ENABLED
                    ? { data: encryptAES(String(data)) }
                    : { data: String(data) }
            }),
            invalidatesTags: (result, error, params) => [
                { type: 'GridData', id: params.url },
                { type: 'GeneralData', id: params.url }
            ],
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse
        }),

        postFormData: builder.mutation({
            query: (params) => {
                const formData = new FormData();
                const obj = params.data;
                for (const key in obj) {
                    if (Object.hasOwn(obj, key)) {
                        const val = obj[key];
                        if (Array.isArray(val) && val.length > 0 && val[0] instanceof File) {
                            val.forEach((f) => formData.append(key, f));
                        } else {
                            formData.append(key, val);
                        }
                    }
                }
                return {
                    url: params.url,
                    body: formData,
                    method: 'POST'
                };
            }

        }),
        postData: builder.mutation({
            query: (params) => ({
                url: params.url,
                body: params.data,
                method: 'POST'
            }),
            invalidatesTags: (result, error, params) => [
                { type: 'GeneralData' },
                { type: 'GridData', id: params.url }
            ],
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse
        }),

        putData: builder.mutation({
            query: (params) => ({
                url: params.url,
                body: IS_ENCRYPTION_ENABLED
                    ? { data: encryptAES(JSON.stringify(params.data)) }
                    : { data: JSON.stringify(params.data) },
                method: 'PUT'
            }),
            invalidatesTags: (result, error, params) => [
                { type: 'GeneralData' },
                { type: 'GridData', id: params.url }
            ],
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse
        }),

        postFileData: builder.mutation({
            query: (params) => ({
                url: params.url,
                body: params.data,
                method: 'POST',
                responseHandler: (response) => response.blob()
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse
        }),


        getFileData: builder.query({
            query: (params) => {
                return {
                    url: params.url,
                    params: params.data,
                    responseHandler: (response) => response.blob()
                };
            },
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse
        })
    })
});

export const {
    useGetGridDataQuery,
    useGetDropdownDataQuery,
    useLazyGetDropdownDataQuery,
    useLazyGetGridDataQuery,
    usePostGridDataMutation,
    useLazyGetDataQuery,
    useGetDataQuery,
    useDeleteDataMutation,
    useRestoreDataMutation,
    usePostFormDataMutation,
    usePostDataMutation,
    usePutDataMutation,
    usePostFileDataMutation,
    useLazyGetFileDataQuery
} = commonApi;